/**
 * «کجا زندگی می‌کنی؟» — کشور، بعد شهر. قرینه‌ی
 * apps/mobile/src/features/profile/WhereYouLive.tsx.
 *
 * ترتیب اتفاقی نیست: شهر تا وقتی کشور معلوم نشه معنی نداره — «Ottawa» توی
 * کانادا و «Ottawa» توی ایلینوی دو جای متفاوتن. با عوض شدنِ کشور، شهر خالی می‌شه.
 *
 * منطقِ بارگذاری (`load`) عمداً همان الگوریتمِ موبایل است، فقط اینجا دوباره
 * نوشته شده به‌جای انتقالِ کامل به core: چندتا خط state-glue است و UI-اش
 * (مربع‌کادر vs. select) آن‌قدر فرق دارد که یک کامپوننتِ مشترک ارزشش را
 * نداشت. خودِ داده (`geoRepo`) از همان core می‌آید.
 */
import React, { useCallback, useState } from 'react';
import { geoRepo } from '@/core/data/geoRepo';
import { reportError } from '@/core/lib/report';
import { t } from '@/core/lib/i18n';
import type { Country } from '@/core/domain/types';

let countriesCache: Promise<Country[]> | null = null;
const allCountries = (): Promise<Country[]> => {
  if (!countriesCache) {
    countriesCache = geoRepo.list().catch((e) => { countriesCache = null; throw e; });
  }
  return countriesCache;
};

export interface WhereYouLiveState {
  country: Country | null;
  city: string;
  /**
   * شناسه‌ی همون شهر توی جدولِ `cities` — چیزی که از مایگریشن 0063 به بعد
   * واقعاً ذخیره می‌شه. اسم برای نشون دادنه، شناسه برای فیلتر کردن.
   */
  cityId: number | null;
  setCountry: (c: Country | null) => void;
  setCity: (c: string, id: number | null) => void;
  load: (country?: string | null, city?: string | null, cityId?: number | null) => void;
}

export function useWhereYouLive(): WhereYouLiveState {
  const [country, setCountry] = useState<Country | null>(null);
  const [city, setCity] = useState('');
  const [cityId, setCityId] = useState<number | null>(null);

  const load = useCallback((
    savedCountry?: string | null, savedCity?: string | null, savedId?: number | null,
  ) => {
    setCountry(null);
    setCity('');
    setCityId(null);
    if (!savedCountry?.trim()) return;
    (async () => {
      const found = await geoRepo.findCountry(savedCountry);
      if (!found) return;
      setCountry(found);
      if (!savedCity?.trim()) return;
      if (await geoRepo.hasCity(found.code, savedCity)) {
        setCity(savedCity.trim());
        setCityId(savedId ?? null);
      }
    })().catch((e) => reportError('web.profile.loadPlace', e));
  }, []);

  return {
    country,
    city,
    cityId,
    setCountry,
    setCity: (c, id) => { setCity(c); setCityId(id); },
    load,
  };
}

/** کمبوباکسِ کوچک: تایپ می‌کنی، فهرست فیلتر می‌شه، از فهرست انتخاب می‌کنی. */
export function Combo({ value, placeholder, disabled, load, onPick }: {
  value: string;
  placeholder: string;
  disabled?: boolean;
  load: (q: string) => Promise<{ key: string; label: string }[]>;
  onPick: (item: { key: string; label: string }) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<{ key: string; label: string }[]>([]);

  const search = (text: string) => {
    setQ(text);
    load(text).then(setItems).catch(() => setItems([]));
  };

  return (
    <div className="combo">
      <input
        className="input"
        disabled={disabled}
        placeholder={placeholder}
        value={open ? q : value}
        onFocus={() => { setOpen(true); search(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => search(e.target.value)}
      />
      {open && items.length > 0 ? (
        <div className="combo-list">
          {items.map((it) => (
            <button key={it.key} type="button" className="combo-item"
              onMouseDown={() => { onPick(it); setOpen(false); }}>
              {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function WhereYouLive({ state }: { state: WhereYouLiveState }) {
  const { country, city, setCountry, setCity } = state;

  /**
   * همه‌ی کشورها، بی‌ هیچ سقفی.
   *
   * تا دیروز `.slice(0, 40)` این‌جا بود و نتیجه‌ش این: کسی که کادر رو باز
   * می‌کرد فقط تا حرفِ C می‌رسید و بقیه‌ی دنیا انگار وجود نداشت — بدونِ هیچ
   * نشانه‌ای که فهرست بریده شده. سقف هم لازم نبود: ۲۵۰ ردیف از قبل روی خودِ
   * مرورگره (نگاه کن به allCountries) و کادرش هم اسکرول داره.
   *
   * شهرها داستانشون فرق داره و سقفشون سمتِ سرور می‌مونه: ۱۵۳ هزار شهر
   * هیچ‌وقت کامل دانلود نمی‌شه، پس اون‌جا باید تایپ کنی — نگاه کن به
   * geoRepo.cities.
   */
  const loadCountries = useCallback(async (q: string) => {
    const list = await allCountries();
    const needle = q.trim().toLowerCase();
    return list
      .filter((c) => !needle || c.name.toLowerCase().includes(needle))
      .map((c) => ({ key: c.code, label: c.emoji ? `${c.emoji}  ${c.name}` : c.name }));
  }, []);

  const loadCities = useCallback(async (q: string) => {
    if (!country) return [];
    const list = await geoRepo.cities(country.code, q);
    return list.map((c) => ({ key: String(c.id), label: c.region ? `${c.name} — ${c.region}` : c.name }));
  }, [country]);

  return (
    <>
      <label className="field">
        <span>{t('countryLabel')}</span>
        <Combo value={country?.name ?? ''} placeholder={t('countryPlaceholder')}
          load={loadCountries}
          onPick={(it) => {
            if (it.key === country?.code) return;
            setCity('', null);
            allCountries()
              .then((list) => setCountry(list.find((c) => c.code === it.key) ?? null))
              .catch((e) => reportError('web.profile.pickCountry', e));
          }} />
      </label>

      <label className="field">
        <span>{t('cityLabel')}</span>
        <Combo value={city} placeholder={t('cityPlaceholder')} disabled={!country}
          load={loadCities}
          onPick={(it) => setCity(it.label.split(' — ')[0], Number(it.key))} />
      </label>
    </>
  );
}
